// Masks panel (§11.4): add linear / radial / brush masks (max 8), select one to edit on the photo, its
// seven adjustments, invert, delete and the overlay (O).
import { Slider } from '../controls/Slider';
import { BrushControls } from './BrushControls';
import { useActive } from './useActive';
import { useUi } from '../../state/ui';
import { useT, type MessageKey } from '../../i18n';
import { MAX_MASKS, newMask } from '../../features/localTools';
import type { LocalMask, MaskAdj, RadialGeom } from '../../model/types';

const ADJ: {
  key: keyof MaskAdj;
  label: MessageKey;
  min: number;
  max: number;
  step: number;
  digits?: number;
}[] = [
  { key: 'exposure', label: 'exposure', min: -4, max: 4, step: 0.01, digits: 2 },
  { key: 'contrast', label: 'contrast', min: -100, max: 100, step: 1 },
  { key: 'highlights', label: 'highlights', min: -100, max: 100, step: 1 },
  { key: 'shadows', label: 'shadows', min: -100, max: 100, step: 1 },
  { key: 'temp', label: 'temp', min: -100, max: 100, step: 1 },
  { key: 'tint', label: 'tint', min: -100, max: 100, step: 1 },
  { key: 'saturation', label: 'saturation', min: -100, max: 100, step: 1 },
];
const NAME: Record<LocalMask['kind'], MessageKey> = {
  linear: 'maskLinear',
  radial: 'maskRadial',
  brush: 'maskBrush',
};
const HINT: Record<LocalMask['kind'], MessageKey> = {
  linear: 'maskHintLinear',
  radial: 'maskHintRadial',
  brush: 'maskHintBrush',
};

export function MasksPanel() {
  const t = useT();
  const { settings, update } = useActive();
  const maskId = useUi((s) => s.maskId);
  const overlay = useUi((s) => s.maskOverlay);
  const masks = settings.masks;
  const index = masks.findIndex((m) => m.id === maskId);
  const sel = index >= 0 ? masks[index]! : null;

  const add = (kind: LocalMask['kind']) => {
    if (masks.length >= MAX_MASKS) return;
    const m = newMask(kind);
    update((s) => void s.masks.push(m));
    useUi.getState().set({ maskId: m.id, tool: 'mask' });
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontSize: 12 }}>{t('addMaskHint')}</span>
        {(['linear', 'radial', 'brush'] as const).map((k) => (
          <button
            key={k}
            data-testid={`add-mask-${k}`}
            disabled={masks.length >= MAX_MASKS}
            title={masks.length >= MAX_MASKS ? t('maskLimit') : undefined}
            onClick={() => add(k)}
          >
            {t(k === 'linear' ? 'addLinear' : k === 'radial' ? 'addRadial' : 'addBrush')}
          </button>
        ))}
      </div>
      {masks.length > 0 && (
        <ul
          role="listbox"
          aria-label={t('panelMasks')}
          style={{ listStyle: 'none', padding: 0, margin: '0 0 6px' }}
        >
          {masks.map((m, i) => {
            const n = masks.slice(0, i + 1).filter((k) => k.kind === m.kind).length;
            return (
              <li key={m.id}>
                <button
                  role="option"
                  aria-selected={m.id === maskId}
                  data-testid={`mask-item-${i}`}
                  onClick={() => useUi.getState().set({ maskId: m.id, tool: 'mask' })}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    ...(m.id === maskId ? { background: 'var(--accent-fill)', color: 'var(--on-accent)' } : {}),
                  }}
                >
                  {t(NAME[m.kind], { n })}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <label style={{ display: 'flex', gap: 6, fontSize: 12, marginBottom: 6 }}>
        <input
          type="checkbox"
          checked={overlay}
          data-testid="mask-overlay"
          onChange={(e) => useUi.getState().set({ maskOverlay: e.target.checked })}
        />
        {t('maskOverlay')}
      </label>
      {sel && (
        <div data-testid="mask-editor">
          <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.8 }}>{t(HINT[sel.kind])}</div>
          {sel.kind === 'brush' && <BrushControls />}
          {sel.kind === 'radial' && (
            <Slider
              label="maskFeather"
              testId="slider-mask-feather"
              value={Math.round((sel.geom as RadialGeom).feather * 100)}
              min={0}
              max={100}
              step={1}
              defaultValue={50}
              onChange={(v, f) =>
                update((s) => void ((s.masks[index]!.geom as RadialGeom).feather = v / 100), f)
              }
            />
          )}
          {ADJ.map((a) => (
            <Slider
              key={a.key}
              label={a.label}
              testId={`slider-mask-${a.key}`}
              value={sel.adj[a.key]}
              min={a.min}
              max={a.max}
              step={a.step}
              digits={a.digits}
              onChange={(v, f) => update((s) => void (s.masks[index]!.adj[a.key] = v), f)}
            />
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <label style={{ display: 'flex', gap: 6, fontSize: 12, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={sel.invert}
                data-testid="mask-invert"
                onChange={(e) => update((s) => void (s.masks[index]!.invert = e.target.checked))}
              />
              {t('maskInvert')}
            </label>
            <button
              data-testid="mask-delete"
              onClick={() => {
                update((s) => void s.masks.splice(index, 1));
                useUi.getState().set({ maskId: null, tool: 'none' });
              }}
            >
              {t('maskDelete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
